/**
 * The app-wide date range, in the header (SellerMate W0.3).
 *
 * Reads "current ▸ compared to" — the window the page is showing, then the
 * window it is compared with — and opens a panel with:
 *   - the same presets as the page picker (DateRangePicker.tsx PRESETS), plus
 *     custom dates;
 *   - a preset's dates are always the window its name says; the days Amazon has
 *     not reported are cut, not excluded (lib/reportGap.ts);
 *   - compared to: no comparison / previous period / same period last year /
 *     custom dates (maths in lib/dateRangeCompare.ts).
 *
 * Mounted once in App.tsx's header. It renders nothing unless a page that
 * uses the shared range (`useAppDateRange`, lib/dateRangeContext.tsx) is on
 * screen, so pages that still keep their own picker never get a second,
 * disconnected control above them. The "compared to" half shows only while
 * something on screen draws the comparison (`useAppCompare`: the Campaigns
 * period tiles); elsewhere it would promise a comparison nobody shows.
 *
 * The button names the PICK (the same dates as the URL and the day tiles). When
 * the pick reaches past the last complete report day, a short warning note
 * ("through Oct 21") shows at every width and the title / panel say the whole
 * sentence (lib/reportGap.ts): the table can only show reported days.
 *
 * Read-only: it only changes which dates pages ask for.
 */
import { useEffect, useRef, useState } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import { PRESETS, rangeFromPreset, type DateRangePreset } from './DateRangePicker'
import { Button, Input, MicroLabel, Select } from './ui'
import { useAppDateRangeControl, resolveAppWindows } from '../lib/dateRangeContext'
import {
  COMPARE_LABELS, COMPARE_MODES, compareWindow, customWindow, fmtWindow,
  previousPeriod, rangeSummary, type CompareMode, type DateWindow,
} from '../lib/dateRangeCompare'
import { usePpcDataThrough } from '../lib/ppcDataThrough'
import { todayYMD } from '../lib/format/date'
import { reportGap, reportGapSentence, reportGapShort } from '../lib/reportGap'

type Control = ReturnType<typeof useAppDateRangeControl>

export default function AppDateRangeControl() {
  const ctl = useAppDateRangeControl()
  if (!ctl.active) return null
  return <ConnectedRangeControl ctl={ctl} />
}

function ConnectedRangeControl({ ctl }: { ctl: Control }) {
  // The same shared freshness read the header's health control makes — no
  // extra request. Only PPC pages move their window to it.
  const dataThrough = usePpcDataThrough()
  return <RangeControl ctl={ctl} dataThrough={dataThrough} />
}

function presetName(preset: DateRangePreset): string {
  if (preset === 'custom') return 'Custom dates'
  return PRESETS.find(p => p.value === preset)?.label ?? 'Custom dates'
}

/** The button and its panel. Takes `dataThrough` instead of reading it, so a
 *  test can render it without the query client. */
export function RangeControl({ ctl, dataThrough }: { ctl: Control; dataThrough: string | null }) {
  const { effective, compare } = resolveAppWindows(ctl.range, ctl.compare, ctl.clampTo, dataThrough)
  const thisYear = Number(todayYMD().slice(0, 4))
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on a click outside — the page picker's idiom (DateRangePicker.tsx).
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const summary = rangeSummary(ctl.range, compare.window, compare.mode, thisYear, ctl.compareShown)
  const gap = reportGap(ctl.range, ctl.clampTo === 'ppc' ? dataThrough : null)
  const gapSentence = reportGapSentence(gap, thisYear)
  const gapShort = reportGapShort(gap)
  const compareText = compare.window
    ? `vs ${fmtWindow(compare.window, thisYear)}`
    : compare.mode === 'custom' ? 'Pick compare dates' : COMPARE_LABELS.none

  return (
    <div ref={ref} className="relative min-w-0" data-testid="app-date-range">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Date range: ${summary}${gapSentence ? ` — ${gapSentence}` : ''}`}
        className="h-8 max-w-[44vw] inline-flex items-center gap-2 px-2.5 rounded-lg border border-edge bg-surface text-sm text-ink hover:bg-surface-2 hover:border-edge-strong transition-colors"
      >
        <Calendar size={14} className="shrink-0 text-ink-faint" aria-hidden />
        <span className="font-medium whitespace-nowrap">{presetName(ctl.range.preset)}</span>
        <span className="hidden lg:inline whitespace-nowrap text-ink-muted">
          {fmtWindow(ctl.range, thisYear)}
        </span>
        {gapShort && (
          <span data-report-gap className="whitespace-nowrap text-warning-soft-fg">· {gapShort}</span>
        )}
        {ctl.compareShown && (
          <>
            <span className="shrink-0 text-ink-faint" aria-hidden>▸</span>
            <span className="truncate text-ink-muted">{compareText}</span>
          </>
        )}
        <ChevronDown size={12} className="shrink-0 text-ink-faint" aria-hidden />
      </button>
      {open && (
        <RangePanel
          ctl={ctl}
          effective={effective}
          compareWindow={compare.window}
          gapSentence={gapSentence}
          summary={summary}
          thisYear={thisYear}
        />
      )}
    </div>
  )
}

export function RangePanel({ ctl, effective, compareWindow: cmp, gapSentence, summary, thisYear }: {
  ctl: Control
  effective: DateWindow
  compareWindow: DateWindow | null
  gapSentence: string | null
  summary: string
  thisYear: number
}) {
  const range = ctl.range
  const isCustom = range.preset === 'custom'

  function pickPreset(p: DateRangePreset) {
    // "Custom dates" starts from the dates already picked; edit them below.
    if (p === 'custom') ctl.pickRange({ from: range.from, to: range.to, preset: 'custom', lag: 0 })
    else ctl.pickRange(rangeFromPreset(p))
  }

  function chooseCompare(mode: CompareMode) {
    if (mode !== 'custom') {
      ctl.setCompare({ mode })
      return
    }
    // Keep typed dates from an earlier custom comparison; otherwise start from
    // the previous period so the two date boxes are never blank.
    const seed = customWindow(ctl.compare.from, ctl.compare.to) ?? previousPeriod(effective)
    ctl.setCompare({ mode: 'custom', from: seed.from, to: seed.to })
  }

  return (
    <div
      role="dialog"
      aria-label="Date range and comparison"
      // Below md (the phone header of W0.5 wraps, so this control can sit at
      // the left of a row): span the screen under the button instead of
      // hanging a right-anchored 22rem panel off the screen's left edge.
      className="absolute right-0 top-full mt-1 z-50 w-[22rem] max-w-[92vw] max-md:fixed max-md:inset-x-2 max-md:top-auto max-md:w-auto max-md:max-w-none rounded-xl border border-edge bg-overlay shadow-pop p-3 space-y-3 text-sm text-ink"
    >
      <section>
        <MicroLabel>Date range</MicroLabel>
        <Select
          aria-label="Date range"
          value={range.preset}
          onChange={e => pickPreset(e.target.value as DateRangePreset)}
          className="mt-1.5 h-8 w-full text-sm"
        >
          {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </Select>
        {isCustom && (
          <CustomDates
            key={`${range.from}|${range.to}`}
            from={range.from}
            to={range.to}
            max={todayYMD()}
            onApply={w => ctl.pickRange({ from: w.from, to: w.to, preset: 'custom', lag: 0 })}
          />
        )}
      </section>

      {ctl.compareShown && (
      <section className="border-t border-edge-subtle pt-3">
        <MicroLabel>Compared to</MicroLabel>
        <div role="radiogroup" aria-label="Compared to" className="mt-1.5 space-y-0.5">
          {COMPARE_MODES.map(mode => {
            const w = mode === 'custom'
              ? (ctl.compare.mode === 'custom' ? cmp : null)
              : compareWindow(effective, { mode })
            return (
              <label
                key={mode}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-surface-2"
              >
                <input
                  type="radio"
                  name="app-date-compare"
                  checked={ctl.compare.mode === mode}
                  onChange={() => chooseCompare(mode)}
                  className="accent-accent"
                />
                <span className="flex-1">{COMPARE_LABELS[mode]}</span>
                {w && <span className="text-xs tabular-nums text-ink-muted">{fmtWindow(w, thisYear)}</span>}
              </label>
            )
          })}
        </div>
        {ctl.compare.mode === 'custom' && (
          <CustomDates
            key={`${ctl.compare.from ?? ''}|${ctl.compare.to ?? ''}`}
            from={ctl.compare.from ?? ''}
            to={ctl.compare.to ?? ''}
            max={todayYMD()}
            onApply={w => ctl.setCompare({ mode: 'custom', from: w.from, to: w.to })}
          />
        )}
      </section>
      )}

      <footer className="border-t border-edge-subtle pt-2 space-y-1 text-xs text-ink-muted">
        <div>Showing {summary}</div>
        {gapSentence && (
          <div className="rounded-md border border-warning-edge bg-warning-soft px-2 py-1 text-warning-soft-fg">
            {gapSentence}
          </div>
        )}
        <div>Days are Pacific time (Amazon&apos;s day). Every page that shows this control uses these dates.</div>
      </footer>
    </div>
  )
}

/** Two date boxes and an Apply button. Edits stay local until Apply, so a
 *  half-typed year (a date box reports 0002, 0020, 0202 on the way to 2026)
 *  never reaches the pages. Reversed dates are swapped on Apply. */
function CustomDates({ from, to, max, onApply }: {
  from: string
  to: string
  max: string
  onApply: (w: DateWindow) => void
}) {
  const [draft, setDraft] = useState({ from, to })
  const win = customWindow(draft.from, draft.to)
  const changed = win !== null && (win.from !== from || win.to !== to)
  return (
    <div className="mt-2 flex items-end gap-2">
      <label className="min-w-0 flex-1">
        <span className="mb-0.5 block text-[11px] text-ink-muted">From</span>
        <Input
          type="date"
          value={draft.from}
          max={max}
          onChange={e => setDraft(d => ({ ...d, from: e.target.value }))}
          className="h-8 w-full px-2 text-xs"
        />
      </label>
      <label className="min-w-0 flex-1">
        <span className="mb-0.5 block text-[11px] text-ink-muted">To</span>
        <Input
          type="date"
          value={draft.to}
          max={max}
          onChange={e => setDraft(d => ({ ...d, to: e.target.value }))}
          className="h-8 w-full px-2 text-xs"
        />
      </label>
      <Button type="button" size="sm" variant="primary" disabled={!changed} onClick={() => { if (win) onApply(win) }}>
        Apply
      </Button>
    </div>
  )
}
